import { STRUCTURE_GRID_SIZE } from 'data/constants';
import { GridSpaceDataType, LevelType } from 'data/types';
import { isPointInPolygon, isPointInsideOrOnPolygon } from 'utils/footprint';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import { unionFootprintShapes } from 'utils/footprint-union';
import { getSpacePolygonPx } from 'utils/point-clamp';

export type RoutePointType = { x: number; y: number };

const VERTEX_INSET_PX = 0.75;
const MAX_ROUTABLE_VERTICES = 120;
const COLLINEAR_DEVIATION_PX = 0.25;

type DuctRouterType = {
  nodes: RoutePointType[];
  staticVisible: boolean[][];
};

const routersByRing = new WeakMap<RoutePointType[], DuctRouterType | null>();

export function resolveRoutingRegionPx(
  structure: LevelType[],
  levelIndex: number,
  getGridSpacesForLevel: (levelIndex: number) => GridSpaceDataType[]
): RoutePointType[][] {
  const footprintsFt = getEffectiveFootprintPolygons(structure[levelIndex]);
  if (footprintsFt.length > 0) {
    return footprintsFt.map((footprintFt) =>
      footprintFt.map((p) => ({
        x: p.x * STRUCTURE_GRID_SIZE,
        y: p.y * STRUCTURE_GRID_SIZE
      }))
    );
  }
  const shapes = getGridSpacesForLevel(levelIndex)
    .map((space) => getSpacePolygonPx(space))
    .filter((polygon) => polygon.length >= 3);
  if (shapes.length === 0) return [];
  return unionFootprintShapes(shapes).outlines;
}

function segmentsProperlyIntersect(
  a1: RoutePointType,
  a2: RoutePointType,
  b1: RoutePointType,
  b2: RoutePointType
): boolean {
  const d = (a2.x - a1.x) * (b2.y - b1.y) - (a2.y - a1.y) * (b2.x - b1.x);
  if (d === 0) return false;
  const t = ((b1.x - a1.x) * (b2.y - b1.y) - (b1.y - a1.y) * (b2.x - b1.x)) / d;
  const u = ((b1.x - a1.x) * (a2.y - a1.y) - (b1.y - a1.y) * (a2.x - a1.x)) / d;
  return t > 0 && t < 1 && u > 0 && u < 1;
}

function segmentInsideRing(
  a: RoutePointType,
  b: RoutePointType,
  ring: RoutePointType[]
): boolean {
  for (let i = 0; i < ring.length; i += 1) {
    const e1 = ring[i];
    const e2 = ring[(i + 1) % ring.length];
    if (segmentsProperlyIntersect(a, b, e1, e2)) return false;
  }
  const fractions = [0.25, 0.5, 0.75];
  return fractions.every((f) =>
    isPointInPolygon(
      { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f },
      ring
    )
  );
}

function insetVertex(
  ring: RoutePointType[],
  index: number
): RoutePointType | null {
  const v = ring[index];
  const prev = ring[(index - 1 + ring.length) % ring.length];
  const next = ring[(index + 1) % ring.length];
  const dpLen = Math.hypot(prev.x - v.x, prev.y - v.y);
  const dnLen = Math.hypot(next.x - v.x, next.y - v.y);
  if (dpLen === 0 || dnLen === 0) return null;
  let dirX = (prev.x - v.x) / dpLen + (next.x - v.x) / dnLen;
  let dirY = (prev.y - v.y) / dpLen + (next.y - v.y) / dnLen;
  const dirLen = Math.hypot(dirX, dirY);
  if (dirLen < 1e-6) {
    dirX = -(next.y - v.y) / dnLen;
    dirY = (next.x - v.x) / dnLen;
  } else {
    dirX /= dirLen;
    dirY /= dirLen;
  }
  const candidate = {
    x: v.x + dirX * VERTEX_INSET_PX,
    y: v.y + dirY * VERTEX_INSET_PX
  };
  if (isPointInPolygon(candidate, ring)) return candidate;
  const flipped = {
    x: v.x - dirX * VERTEX_INSET_PX,
    y: v.y - dirY * VERTEX_INSET_PX
  };
  return isPointInPolygon(flipped, ring) ? flipped : null;
}

function buildRouter(ring: RoutePointType[]): DuctRouterType | null {
  if (ring.length < 3 || ring.length > MAX_ROUTABLE_VERTICES) return null;
  const nodes = ring
    .map((_, index) => insetVertex(ring, index))
    .filter((node): node is RoutePointType => node !== null);
  if (nodes.length === 0) return null;
  const staticVisible: boolean[][] = nodes.map(() =>
    new Array<boolean>(nodes.length).fill(false)
  );
  for (let i = 0; i < nodes.length; i += 1) {
    for (let j = i + 1; j < nodes.length; j += 1) {
      const visible = segmentInsideRing(nodes[i], nodes[j], ring);
      staticVisible[i][j] = visible;
      staticVisible[j][i] = visible;
    }
  }
  return { nodes, staticVisible };
}

function routerForRing(ring: RoutePointType[]): DuctRouterType | null {
  if (routersByRing.has(ring)) return routersByRing.get(ring) ?? null;
  const router = buildRouter(ring);
  routersByRing.set(ring, router);
  return router;
}

function perpendicularDeviationPx(
  prev: RoutePointType,
  point: RoutePointType,
  next: RoutePointType
): number {
  const baseLen = Math.hypot(next.x - prev.x, next.y - prev.y);
  if (baseLen === 0) return 0;
  const cross =
    (point.x - prev.x) * (next.y - prev.y) -
    (point.y - prev.y) * (next.x - prev.x);
  return Math.abs(cross) / baseLen;
}

function simplifyCollinear(path: RoutePointType[]): RoutePointType[] {
  if (path.length <= 2) return path;
  const simplified: RoutePointType[] = [path[0]];
  for (let i = 1; i < path.length - 1; i += 1) {
    const prev = simplified[simplified.length - 1];
    const next = path[i + 1];
    if (
      perpendicularDeviationPx(prev, path[i], next) > COLLINEAR_DEVIATION_PX
    ) {
      simplified.push(path[i]);
    }
  }
  simplified.push(path[path.length - 1]);
  return simplified;
}

function shortestPathThroughNodes(
  router: DuctRouterType,
  ring: RoutePointType[],
  fromPx: RoutePointType,
  toPx: RoutePointType
): RoutePointType[] | null {
  const nodeCount = router.nodes.length + 2;
  const points: RoutePointType[] = [fromPx, toPx, ...router.nodes];
  const visible = (i: number, j: number): boolean => {
    if (i > 1 && j > 1) return router.staticVisible[i - 2][j - 2];
    if (i <= 1 && j <= 1) return false;
    return segmentInsideRing(points[i], points[j], ring);
  };

  const dist = new Array<number>(nodeCount).fill(Number.POSITIVE_INFINITY);
  const prev = new Array<number>(nodeCount).fill(-1);
  const done = new Array<boolean>(nodeCount).fill(false);
  dist[0] = 0;

  for (let iter = 0; iter < nodeCount; iter += 1) {
    let current = -1;
    let best = Number.POSITIVE_INFINITY;
    for (let i = 0; i < nodeCount; i += 1) {
      if (!done[i] && dist[i] < best) {
        best = dist[i];
        current = i;
      }
    }
    if (current === -1) break;
    if (current === 1) break;
    done[current] = true;
    for (let next = 0; next < nodeCount; next += 1) {
      if (!done[next] && visible(current, next)) {
        const candidate =
          dist[current] +
          Math.hypot(
            points[next].x - points[current].x,
            points[next].y - points[current].y
          );
        if (candidate < dist[next]) {
          dist[next] = candidate;
          prev[next] = current;
        }
      }
    }
  }

  if (!Number.isFinite(dist[1])) return null;
  const indices: number[] = [];
  let cursor = 1;
  while (cursor !== -1) {
    indices.push(cursor);
    cursor = prev[cursor];
  }
  indices.reverse();
  return indices.map((i) => points[i]);
}

export function routeDuctPath(
  regionPx: RoutePointType[][],
  fromPx: RoutePointType,
  toPx: RoutePointType
): RoutePointType[] {
  const straight = [fromPx, toPx];
  const ring = regionPx.find(
    (r) =>
      r.length >= 3 &&
      isPointInsideOrOnPolygon(fromPx, r) &&
      isPointInsideOrOnPolygon(toPx, r)
  );
  if (!ring) return straight;
  if (segmentInsideRing(fromPx, toPx, ring)) return straight;
  const router = routerForRing(ring);
  if (!router) return straight;
  const path = shortestPathThroughNodes(router, ring, fromPx, toPx);
  if (!path) return straight;
  return simplifyCollinear(path);
}

export function polylineLengthPx(path: RoutePointType[]): number {
  let total = 0;
  for (let i = 1; i < path.length; i += 1) {
    total += Math.hypot(path[i].x - path[i - 1].x, path[i].y - path[i - 1].y);
  }
  return total;
}

const MIN_BEND_DEVIATION_PX = 8;

function isBendPoint(
  prev: RoutePointType,
  point: RoutePointType,
  next: RoutePointType
): boolean {
  const inX = point.x - prev.x;
  const inY = point.y - prev.y;
  const outX = next.x - point.x;
  const outY = next.y - point.y;
  if (inX * outX + inY * outY < 0) return true;
  return perpendicularDeviationPx(prev, point, next) > MIN_BEND_DEVIATION_PX;
}

export function bendCount(path: RoutePointType[]): number {
  let bends = 0;
  for (let i = 1; i < path.length - 1; i += 1) {
    if (isBendPoint(path[i - 1], path[i], path[i + 1])) {
      bends += 1;
    }
  }
  return bends;
}

export function routePathInteriorWaypointsPx(
  path: RoutePointType[]
): Array<{ uniqueId: string; x: number; y: number }> {
  return path.slice(1, -1).map((point, index) => ({
    uniqueId: `route-${index}`,
    x: point.x,
    y: point.y
  }));
}

export function midpointAlongPath(path: RoutePointType[]): RoutePointType {
  if (path.length === 0) return { x: 0, y: 0 };
  if (path.length === 1) return path[0];
  const half = polylineLengthPx(path) / 2;
  let walked = 0;
  for (let i = 1; i < path.length; i += 1) {
    const segLen = Math.hypot(
      path[i].x - path[i - 1].x,
      path[i].y - path[i - 1].y
    );
    if (segLen > 0 && walked + segLen >= half) {
      const t = (half - walked) / segLen;
      return {
        x: path[i - 1].x + (path[i].x - path[i - 1].x) * t,
        y: path[i - 1].y + (path[i].y - path[i - 1].y) * t
      };
    }
    walked += segLen;
  }
  return path[path.length - 1];
}

export function pathToSvgPoints(path: RoutePointType[]): string {
  return path.map((p) => `${p.x},${p.y}`).join(' ');
}
